import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";
import { formatStamp } from "../lib/richText";
import { Button } from "../components/ui/button";
import { ScoreHeader } from "../components/prospect-audit/ScoreHeader";
import { FindingsCard, SeoAeoCard, DesignCard } from "../components/prospect-audit/FindingsCard";
import { GoogleCheck } from "../components/prospect-audit/GoogleCheck";
import { ArrowLeft, ExternalLink, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function ProspectAuditDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [audit, setAudit] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let timer;
    const load = () => axios.get(`${API}/prospect-audits/${id}`)
      .then(({ data }) => { setAudit(data); if (data.status === "running") timer = setTimeout(load, 4000); })
      .catch((e) => setError(e.response?.data?.detail || "Could not load the report"));
    load();
    return () => clearTimeout(timer);
  }, [id]);

  const remove = async () => {
    if (!window.confirm("Delete this report?")) return;
    await axios.delete(`${API}/prospect-audits/${id}`);
    toast.success("Report deleted");
    navigate("/prospect-iq/audits");
  };

  if (error) return <p className="text-sm text-red-600" data-testid="pa-load-error">{error}</p>;
  if (!audit) return <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />;
  return (
    <div className="space-y-5" data-testid="prospect-audit-detail">
      <button type="button" onClick={() => navigate("/prospect-iq/audits")} className="text-sm text-muted-foreground flex items-center gap-1 hover:text-foreground" data-testid="pa-back">
        <ArrowLeft className="h-4 w-4" />All reports
      </button>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-outfit text-3xl font-bold tracking-tight" data-testid="pa-title">{audit.business_name}</h1>
          <p className="text-sm text-muted-foreground">
            <a href={audit.website_url} target="_blank" rel="noopener noreferrer" className="underline">{audit.grader?.final_url || audit.website_url}</a>
            {audit.town ? ` · ${audit.town}` : ""} · {audit.created_by_name} · {formatStamp(audit.created_at)}
          </p>
        </div>
        <div className="flex gap-2">
          {audit.status === "running" && <span className="text-xs flex items-center gap-1 text-amber-700" data-testid="pa-running"><Loader2 className="h-3 w-3 animate-spin" />Building report…</span>}
          {audit.grader?.report_url && !audit.grader.report_url.includes("localhost") && <Button variant="outline" size="sm" asChild><a href={audit.grader.report_url} target="_blank" rel="noopener noreferrer" data-testid="pa-grader-link"><ExternalLink className="h-4 w-4 mr-1" />Full check list</a></Button>}
          <Button variant="ghost" size="sm" onClick={remove} data-testid="pa-delete"><Trash2 className="h-4 w-4 text-red-600" /></Button>
        </div>
      </div>
      {audit.steps?.grade === "error" && <p className="text-sm text-amber-700" data-testid="pa-grade-error">Website scan: {audit.errors?.grade}</p>}
      <ScoreHeader audit={audit} />
      <DesignCard design={audit.design} error={audit.errors?.design} pending={audit.steps?.design === "running"} />
      {audit.wants_google && <GoogleCheck audit={audit} />}
      <div className="grid lg:grid-cols-2 gap-5">
        <FindingsCard grader={audit.grader} />
        <SeoAeoCard seo={audit.seo_aeo} error={audit.errors?.seo} />
      </div>
    </div>
  );
}
